import type { AgGridReact } from '@mint-lib/ag-grid/react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';

import type { GridApi } from '@mint-lib/ag-grid/community';
import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { FolderAdd, Information } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  Card,
  Divider,
  typographyVariants,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { Chat } from '@mint-sdk/api-ai-assistant';
import { useAtom } from 'jotai';

import type { MoveRejection } from '../../helpers/fileSystemMove.js';
import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import { useFileSystemNodes } from '../../hooks/useFileSystemNodes.js';
import { useFolderNavigation } from '../../hooks/useFolderNavigation.js';
import { useMoveNodes } from '../../hooks/useMoveNodes.js';
import { getDocumentsOption } from '../../queries/rag.js';
import { folderPathAtom } from '../../store/fileSystem.js';
import {
  MAX_FOLDER_DEPTH,
  ROOT_FOLDER_ID,
  type FileNode,
  type FileSystemNode,
  type FolderNode,
} from '../../types/fileSystem.js';
import { RagTableType, type FileSystemViewType } from '../../types/rag.js';
import { DeleteNodesDialog } from './DeleteNodesDialog.jsx';
import { EmptyState } from './EmptyState.jsx';
import { FileSystemBreadcrumbs } from './FileSystem/Breadcrumbs/Breadcrumbs.jsx';
import { FileSystemDndProvider } from './FileSystem/dnd/FileSystemDnd.jsx';
import { FileSystemUsage } from './FileSystem/FileSystemUsage/FileSystemUsage.jsx';
import { FolderSection } from './FileSystem/FolderSection/FolderSection.jsx';
import { useCreateFolder } from './FileSystem/FolderSection/useCreateFolder.js';
import { MoveNodesDialog } from './FileSystem/MoveNodesDialog/MoveNodesDialog.jsx';
import { PrivateFolder } from './FileSystem/PrivateFolder/PrivateFolder.jsx';
import { FileSystemTable } from './FileSystem/TableView/FileSystemTable.jsx';
import { FileSystemTileExplorer } from './FileSystem/TileView/TileExplorer.jsx';
import { FileSystemViewSwitcher } from './FileSystem/ViewSwitcher/ViewSwitcher.jsx';
import { KnowledgeBaseNoRowOverlay } from './RagTable/NoRowsOverlay.jsx';
import { RagTable } from './RagTable/RagTable.jsx';
import { RulesDialog } from './RulesDialog.jsx';
import { EditPermissionsDialog } from './ShareFiles/EditPermissionsDialog.jsx';
import { ToolbarActions } from './ToolbarActions.jsx';
import { UploadFileDialog } from './UploadFileDialog.jsx';
import { useDocumentActions } from './useDocumentActions.js';

const KnowledgeBase = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', [
    'aiFileSystem',
    'aiKnowledgeBaseSearchbar',
    'aiKnowledgeBaseStorage',
  ]);

  const gridRef = useRef<AgGridReact | null>(null);
  // In state so the breadcrumbs can register as an AG Grid drop zone as soon
  // as the grid is ready, and drop it again when the view switches away.
  const [gridApi, setGridApi] = useState<GridApi<FileSystemNode> | null>(null);

  const [selectedRows, setSelectedRows] = useState<RagTableType[]>([]);
  const [deletableRows, setDeletableRows] = useState<RagTableType[]>([]);
  const [deletableFolders, setDeletableFolders] = useState<FolderNode[]>([]);
  const [editableAccessRow, setEditableAccessRow] =
    useState<RagTableType | null>(null);

  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenMoveDialog, setIsOpenMoveDialog] = useState(false);
  const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
  const [isOpenRulesDialog, setIsOpenRulesDialog] = useState(false);

  const [view, setView] = useState<FileSystemViewType>('table');

  const [path, setPath] = useAtom(folderPathAtom);

  const { currentFolderId, depth, enterFolder, goToCrumb } =
    useFolderNavigation({ path, setPath });

  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);

  const breadcrumbItems = useMemo(
    () => [{ folderId: ROOT_FOLDER_ID, name: t('My Files') }, ...path],
    [path, t],
  );

  const { onDocumentsBulkDownload } = useDocumentActions();
  const { canUploadKBFile, canDeleteKBFile, canDownloadKBFile } =
    useAiHubPermissions();

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const handleChangeSearch = useCallback(({ target }: ChangeEvent) => {
    setSearchValue((target as HTMLInputElement).value);
  }, []);

  const handleClearSearch = useCallback(() => setSearchValue(''), []);

  const isStorageAvailable =
    ff.aiKnowledgeBaseStorage.enabled && canUploadKBFile;

  const { data: documentData = [] } = useQuery(getDocumentsOption());
  const {
    data: storageData,
    isLoading: isStorageLoading,
    isError: isStorageError,
  } = useQuery({
    ...Chat.getKnowledgeBaseStorage({}),
    enabled: isStorageAvailable,
  });

  const tableData = useMemo(() => {
    return documentData
      .filter(
        (item) =>
          !ff.aiKnowledgeBaseSearchbar.enabled ||
          item.filename
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()),
      )
      .map((d) => ({
        fileId: d.fileId,
        fileName: d.filename,
        fileType: d.type,
        description: d.description,
        uploadedDate: d.createdAt,
        firstName: d.firstName,
        lastName: d.lastName,
        userId: d.userId,
        status: d.status,
      }));
  }, [documentData, debouncedSearchValue, ff.aiKnowledgeBaseSearchbar.enabled]);

  const {
    folders,
    files,
    isLoading: isLoadingNodes,
  } = useFileSystemNodes({
    folderId: currentFolderId,
    depth,
    search: ff.aiKnowledgeBaseSearchbar.enabled ? debouncedSearchValue : '',
  });

  const {
    isDrafting,
    startDraft,
    cancelDraft,
    commitDraft,
    renameFolder,
    validateName,
  } = useCreateFolder({ parentId: currentFolderId, depth });

  const canCreateFolder = canUploadKBFile && depth < MAX_FOLDER_DEPTH;

  const selectedCount = selectedRows.length + selectedFolderIds.length;
  const hasSelectedFolders = selectedFolderIds.length > 0;
  const hasValidFileSelection = selectedRows.some((row) =>
    documentData.some((document) => document.fileId === row.fileId),
  );

  const selectedFolders = useMemo(
    () => folders.filter((folder) => selectedFolderIds.includes(folder.id)),
    [folders, selectedFolderIds],
  );

  // Folders are managed with the upload permission, files with the delete one,
  // so a mixed selection needs both before the toolbar bin unlocks.
  const canDeleteSelection =
    (hasSelectedFolders || hasValidFileSelection) &&
    (!hasSelectedFolders || canUploadKBFile) &&
    (!hasValidFileSelection || canDeleteKBFile);

  const selectedFileIds = useMemo(
    () => selectedRows.map((row) => row.fileId),
    [selectedRows],
  );

  const onToggleFolder = useCallback((id: string) => {
    setSelectedFolderIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const onToggleFile = useCallback((file: RagTableType) => {
    setSelectedRows((prev) =>
      prev.some((row) => row.fileId === file.fileId)
        ? prev.filter((row) => row.fileId !== file.fileId)
        : [...prev, file],
    );
  }, []);

  const nodes = useMemo<FileSystemNode[]>(
    () => [...folders, ...files],
    [folders, files],
  );

  const onCommitRename = useCallback(
    (id: string, name: string) => {
      const renamed = renameFolder(id, name);

      if (renamed) setRenamingFolderId(null);

      return renamed;
    },
    [renameFolder],
  );

  const onCancelRename = useCallback(() => setRenamingFolderId(null), []);

  const onSelectNodes = useCallback((selected: FileSystemNode[]) => {
    setSelectedRows(
      selected.filter((node): node is FileNode => node.kind === 'file'),
    );
    setSelectedFolderIds(
      selected.filter((node) => node.kind === 'folder').map((node) => node.id),
    );
  }, []);

  const hasActiveSearch =
    ff.aiKnowledgeBaseSearchbar.enabled &&
    debouncedSearchValue.trim().length > 0;

  const hasNoSearchResults =
    ff.aiFileSystem.enabled &&
    hasActiveSearch &&
    !folders.length &&
    !files.length;

  const showEmptyState = ff.aiFileSystem.enabled
    ? !isLoadingNodes &&
      !folders.length &&
      !files.length &&
      !isDrafting &&
      !hasActiveSearch
    : !documentData.length;

  const onOpenUploadDialog = () => {
    setIsOpenUploadDialog(true);
  };

  const onCloseUploadDialog = () => {
    setIsOpenUploadDialog(false);
  };

  const handleToggleRulesDialog = () => {
    setIsOpenRulesDialog((p) => !p);
  };

  const onSelectTableRows = useCallback((rows: RagTableType[]) => {
    setSelectedRows(rows);
  }, []);
  const onSelectDeletableTableRows = useCallback((rows: RagTableType[]) => {
    setDeletableRows(rows);
  }, []);

  const selectedNodes = useMemo<FileSystemNode[]>(
    () => [
      ...selectedFolders,
      ...selectedRows.map((row) => ({
        kind: 'file' as const,
        id: row.fileId,
        ...row,
      })),
    ],
    [selectedFolders, selectedRows],
  );

  const [movableNodes, setMovableNodes] =
    useState<FileSystemNode[]>(selectedNodes);

  const onUnselectTableRows = useCallback(() => {
    setSelectedRows([]);
    setMovableNodes([]);
    setSelectedFolderIds([]);
    gridRef.current?.api?.deselectAll();
  }, []);

  const handleChangeView = useCallback(
    (next: FileSystemViewType) => {
      if (next === view) return;

      onUnselectTableRows();
      setView(next);
    },
    [view, onUnselectTableRows],
  );

  const resetFolderScopedState = useCallback(() => {
    onUnselectTableRows();
    setRenamingFolderId(null);
    cancelDraft();
  }, [onUnselectTableRows, cancelDraft]);

  const handleOpenFolder = useCallback(
    (folder: FolderNode) => {
      resetFolderScopedState();
      enterFolder(folder);
    },
    [resetFolderScopedState, enterFolder],
  );

  const handleGoToCrumb = useCallback(
    (folderId: string) => {
      resetFolderScopedState();
      goToCrumb(folderId);
    },
    [resetFolderScopedState, goToCrumb],
  );

  const onOpenDeleteDialog = useCallback(() => {
    setIsOpenDeleteDialog(true);
  }, []);
  const onCloseDeleteDialog = useCallback(() => {
    setIsOpenDeleteDialog(false);
  }, []);

  const handleOpenDeleteDialog = useCallback(
    (documents: RagTableType[], foldersToDelete: FolderNode[] = []) => {
      onSelectDeletableTableRows(documents);
      setDeletableFolders(foldersToDelete);
      onOpenDeleteDialog();
    },
    [onSelectDeletableTableRows, onOpenDeleteDialog],
  );

  const handleOpenDeleteSelection = useCallback(
    () => handleOpenDeleteDialog(selectedRows, selectedFolders),
    [handleOpenDeleteDialog, selectedRows, selectedFolders],
  );

  const handleDownloadSelection = () =>
    onDocumentsBulkDownload(selectedRows, onUnselectTableRows);

  const handleOpenDeleteFolder = useCallback(
    (folder: FolderNode) => handleOpenDeleteDialog([], [folder]),
    [handleOpenDeleteDialog],
  );

  const handleCloseEditPermissionsDialog = useCallback(
    () => setEditableAccessRow(null),
    [],
  );

  const { checkMove, moveNodes } = useMoveNodes();

  const describeRejection = useCallback(
    (reason: MoveRejection) => {
      switch (reason) {
        case 'descendant':
        case 'self':
          return t("A folder can't be moved inside itself");
        case 'max-depth':
          return t('Folders can only be nested {{levels}} levels deep', {
            levels: MAX_FOLDER_DEPTH,
          });
        case 'name-taken':
          return t('An item with this name already exists in the folder');
        case 'file-not-ready':
          return t('Files still being processed can not be moved');
        default:
          return null;
      }
    },
    [t],
  );

  const handleMoveNodes = useCallback(
    (nodes: FileSystemNode[], targetFolderId: string) => {
      const verdict = moveNodes(nodes, targetFolderId);

      if (verdict.allowed) {
        onUnselectTableRows();
        return;
      }

      const message = describeRejection(verdict.reason);

      // No-op rejections explain themselves by the drop being refused; only
      // the surprising ones get a snackbar.
      if (!message) return;

      enqueueSnackbar({
        variant: 'warning',
        subTitle: message,
        removable: true,
        position: 'top-center',
        className: 'flex self-center',
      });
    },
    [moveNodes, onUnselectTableRows, describeRejection],
  );

  const onOpenMoveDialog = (selectedNodes: FileSystemNode[]) => {
    setMovableNodes(selectedNodes);
    setIsOpenMoveDialog(true);
  };

  return (
    <>
      <div className="flex h-full flex-col gap-5">
        {ff.aiFileSystem.enabled && (
          <>
            <div className="flex flex-col gap-4">
              <div
                className={cn(
                  'uppercase',
                  typographyVariants({
                    variant: 'h5',
                    color: 'primary',
                  }),
                )}
              >
                {t('Private folders')}
              </div>
              <div className="flex flex-row gap-4">
                <PrivateFolder
                  folderName="Users private folders"
                  onOpenFolder={() => {
                    console.log('open private users folder');
                  }}
                />
                <PrivateFolder
                  folderName="My private folder"
                  onOpenFolder={() => {
                    console.log('open private folder');
                  }}
                />
              </div>
            </div>
            <Divider />
            <div
              className={cn(
                'uppercase',
                typographyVariants({
                  variant: 'h5',
                  color: 'primary',
                }),
              )}
            >
              {t('Shared folders')}
            </div>
          </>
        )}
        <Card className="flex w-full flex-row" contentClassName="w-full">
          <div className="flex w-full flex-row justify-between">
            <div className="flex flex-row items-center gap-3">
              <ToolbarActions
                selectedCount={selectedCount}
                isFileSystemEnabled={ff.aiFileSystem.enabled}
                canUploadKBFile={canUploadKBFile}
                canDownloadKBFile={canDownloadKBFile}
                canDeleteSelection={canDeleteSelection}
                hasSelectedFolders={hasSelectedFolders}
                onOpenUploadDialog={onOpenUploadDialog}
                onDownloadSelection={handleDownloadSelection}
                onDeleteSelection={handleOpenDeleteSelection}
                onOpenMoveDialog={() => onOpenMoveDialog(selectedNodes)}
              />
              {ff.aiFileSystem.enabled && canCreateFolder && !selectedCount && (
                <Button
                  variant={'outlined'}
                  onClick={startDraft}
                  data-dd-action-name="Knowledge base: create folder"
                  disabled={isDrafting}
                >
                  <FolderAdd />
                  {t('Create folder')}
                </Button>
              )}
            </div>

            <div className="flex flex-row gap-2">
              {canUploadKBFile && !ff.aiKnowledgeBaseStorage.enabled && (
                <Button
                  variant="text"
                  className="flex flex-row gap-2"
                  data-testid="uploading-rules-btn"
                  onClick={handleToggleRulesDialog}
                >
                  <Information />
                  {t('Uploading rules')}
                </Button>
              )}
              {ff.aiFileSystem.enabled && (
                <FileSystemViewSwitcher
                  view={view}
                  setView={handleChangeView}
                />
              )}
              {ff.aiKnowledgeBaseSearchbar.enabled && (
                <SearchBar
                  placeholder={
                    ff.aiFileSystem.enabled
                      ? t('Search a file or a folder')
                      : t('Search a file')
                  }
                  className="h-8 w-75 max-w-75"
                  onChange={handleChangeSearch}
                  onClear={handleClearSearch}
                  value={searchValue}
                />
              )}
            </div>
          </div>
        </Card>

        <FileSystemDndProvider
          selectedNodes={selectedNodes}
          checkMove={checkMove}
          onMoveNodes={handleMoveNodes}
        >
          {ff.aiFileSystem.enabled && (
            <FileSystemBreadcrumbs
              items={breadcrumbItems}
              onFolderChange={handleGoToCrumb}
              gridApi={gridApi}
            />
          )}

          {ff.aiFileSystem.enabled && view === 'tiles' && (
            <FolderSection
              folders={folders}
              selectedFolderIds={selectedFolderIds}
              onToggleFolder={onToggleFolder}
              onOpenFolder={handleOpenFolder}
              isDrafting={isDrafting}
              onCommitDraft={commitDraft}
              onCancelDraft={cancelDraft}
              renamingFolderId={renamingFolderId}
              onStartRename={setRenamingFolderId}
              onDeleteFolder={handleOpenDeleteFolder}
              onOpenMoveDialog={onOpenMoveDialog}
              onCommitRename={onCommitRename}
              onCancelRename={onCancelRename}
              validateName={validateName}
              className="grid grid-cols-4"
            />
          )}

          {!showEmptyState &&
            view === 'table' &&
            (ff.aiFileSystem.enabled ? (
              <FileSystemTable
                gridRef={gridRef}
                tableData={nodes}
                isDrafting={isDrafting}
                onSelectNodes={onSelectNodes}
                onUnselectTableRows={onUnselectTableRows}
                onOpenDeleteDialog={handleOpenDeleteDialog}
                onOpenDeleteFolder={handleOpenDeleteFolder}
                onOpenEditPermissionsDialog={setEditableAccessRow}
                onOpenFolder={handleOpenFolder}
                onOpenMoveDialog={onOpenMoveDialog}
                onStartRename={setRenamingFolderId}
                renamingFolderId={renamingFolderId}
                onCommitRename={onCommitRename}
                onCancelRename={onCancelRename}
                onCommitDraft={commitDraft}
                onCancelDraft={cancelDraft}
                validateName={validateName}
                checkMove={checkMove}
                onMoveNodes={handleMoveNodes}
                onGridApiChange={setGridApi}
              />
            ) : (
              <RagTable
                gridRef={gridRef}
                tableData={tableData}
                onSelectTableRows={onSelectTableRows}
                onUnselectTableRows={onUnselectTableRows}
                onOpenDeleteDialog={handleOpenDeleteDialog}
                onOpenEditPermissionsDialog={setEditableAccessRow}
              />
            ))}

          {!showEmptyState && view === 'tiles' && hasNoSearchResults && (
            <KnowledgeBaseNoRowOverlay />
          )}

          {!showEmptyState && view === 'tiles' && !hasNoSearchResults && (
            <FileSystemTileExplorer
              onOpenMoveDialog={onOpenMoveDialog}
              tableData={ff.aiFileSystem.enabled ? files : tableData}
              selectedFileIds={selectedFileIds}
              onToggleFile={onToggleFile}
              onUnselectTableRows={onUnselectTableRows}
              onOpenDeleteDialog={handleOpenDeleteDialog}
              onOpenEditPermissionsDialog={setEditableAccessRow}
            />
          )}

          {showEmptyState && (
            <EmptyState
              isFileSystemEnabled={ff.aiFileSystem.enabled}
              depth={depth}
              canUploadKBFile={canUploadKBFile}
              onOpenUploadDialog={onOpenUploadDialog}
            />
          )}
        </FileSystemDndProvider>
        {isStorageAvailable && (
          <FileSystemUsage
            onOpenRules={handleToggleRulesDialog}
            storageData={storageData}
            isLoading={isStorageLoading}
            isError={isStorageError}
          />
        )}
      </div>

      <UploadFileDialog
        isOpen={isOpenUploadDialog}
        onClose={onCloseUploadDialog}
        documents={documentData}
        targetFolderId={currentFolderId}
      />

      <RulesDialog
        isOpen={isOpenRulesDialog}
        onClose={handleToggleRulesDialog}
      />

      <DeleteNodesDialog
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        documents={deletableRows}
        folders={deletableFolders}
        onUnselectTableRows={onUnselectTableRows}
      />

      {ff.aiFileSystem.enabled && (
        <EditPermissionsDialog
          isOpen={Boolean(editableAccessRow)}
          onClose={handleCloseEditPermissionsDialog}
          document={editableAccessRow}
        />
      )}

      {ff.aiFileSystem.enabled && isOpenMoveDialog && (
        <MoveNodesDialog
          nodes={movableNodes}
          onMoveNodes={handleMoveNodes}
          canMoveNodes={checkMove}
          open={isOpenMoveDialog}
          onClose={() => setIsOpenMoveDialog(false)}
        />
      )}
    </>
  );
};

export default KnowledgeBase;
